import { Button } from "@/components/flexkit/Button";
import { ScrollArea } from "@/components/flexkit/ScrollArea";
import { Checkbox } from "@/components/flexkit/checkbox/Checkbox";
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogBody,
} from "@/components/podkit/dialog/Dialog";
import { cn } from "@/components/podkit/lib/cn";
import { Text } from "@/components/podkit/typography/Text";

import { IconEdit } from "@/assets/icons/geist/IconEdit";
import { IconTrash } from "@/assets/icons/geist/IconTrash";
import { IconArrowUp } from "@/assets/icons/geist/IconArrowUp";
import { type FC, useState, useCallback, useMemo } from "react";
import type { ReviewComment, CachedCodeLine, PendingCommentSelection } from "@/components/ai/review/types";
import { Markdown } from "@/components/ai/chat/Markdown";
import { CodeContextPreview } from "@/components/ai/review/CodeContextPreview";
import { FileChangeList } from "@/routes/environments/code-changes/FileChangeList";
import { GitStatusPanelCompact } from "@/routes/environments/details/GitStatusPanelCompact";
import type { PlainEnvironment } from "@/queries/environment-queries";
import type { EnvironmentGitStatus } from "gitpod-next-api/gitpod/v1/environment_pb";
import { GitPullRequest } from "lucide-react";


export type ReviewTabProps = {
    environment: PlainEnvironment;
    comments: ReviewComment[];
    onEditComment: (commentId: string, content: string) => void;
    onRemoveComment: (commentId: string) => void;
    onNavigateToComment: (comment: ReviewComment) => void;
    /** Called with selected comments (or all if none selected) and optional additional context */
    onSubmitReview: (selectedComments: ReviewComment[], additionalContext?: string) => void;
    onDismissReview: () => void;
    /** Called when user wants to start a review */
    onStartReview?: () => void;
    /** Whether the start review action is disabled (e.g., agent is busy) */
    isStartReviewDisabled?: boolean;
    isSubmitting?: boolean;
    className?: string;
    /** Pending selection for adding a new comment */
    pendingSelection?: PendingCommentSelection | null;
    /** Callback when user adds a comment from the pending selection */
    onAddComment?: (filePath: string, startLine: number, endLine: number, content: string) => void;
    /** Callback to clear the pending selection */
    onClearPendingSelection?: () => void;
    /** Callback to update a comment's code context (hash and cached lines) */
    onUpdateCodeContext?: (commentId: string, hash: string, lines: CachedCodeLine[]) => void;
    /** Git status for showing code changes */
    gitStatus?: EnvironmentGitStatus;
    /** Callback when user selects lines for a new comment in the diff */
    onLineSelectionForComment?: (selection: PendingCommentSelection) => void;
    /** Callback when user clicks "Create Pull Request" button */
    onCreatePullRequest?: () => void;
    /** Whether the create PR action is disabled (e.g., agent is busy) */
    isCreatePullRequestDisabled?: boolean;
};

export const ReviewTab: FC<ReviewTabProps> = ({
    environment,
    comments,
    onEditComment,
    onRemoveComment,
    onNavigateToComment,
    onSubmitReview,
    onDismissReview,
    onStartReview,
    isStartReviewDisabled = false,
    isSubmitting = false,
    className,
    pendingSelection,
    onAddComment,
    onClearPendingSelection,
    onUpdateCodeContext,
    gitStatus,
    onLineSelectionForComment,
    onCreatePullRequest,
    isCreatePullRequestDisabled = false,
}) => {
    const [openedFiles, setOpenedFiles] = useState<string[]>([]);
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editContent, setEditContent] = useState("");
    const [selectedCommentIds, setSelectedCommentIds] = useState<Set<string>>(new Set());
    const [showSubmitForm, setShowSubmitForm] = useState(false);
    const [additionalContext, setAdditionalContext] = useState("");

    const toggleCommentSelection = useCallback((commentId: string) => {
        setSelectedCommentIds((prev) => {
            const next = new Set(prev);
            if (next.has(commentId)) {
                next.delete(commentId);
            } else {
                next.add(commentId);
            }
            return next;
        });
    }, []);

    const selectedComments = useMemo(() => {
        if (selectedCommentIds.size === 0) {
            return comments;
        }
        return comments.filter((c) => selectedCommentIds.has(c.id));
    }, [comments, selectedCommentIds]);

    const handleSubmitClick = useCallback(() => {
        setShowSubmitForm(true);
    }, []);

    const handleConfirmSubmit = useCallback(() => {
        onSubmitReview(selectedComments, additionalContext.trim() || undefined);
        setShowSubmitForm(false);
        setAdditionalContext("");
        setSelectedCommentIds(new Set());
    }, [onSubmitReview, selectedComments, additionalContext]);

    const handleCancelSubmit = useCallback(() => {
        setShowSubmitForm(false);
        setAdditionalContext("");
    }, []);

    const handleStartEdit = useCallback((comment: ReviewComment) => {
        setEditingCommentId(comment.id);
        setEditContent(comment.content);
    }, []);

    const handleSaveEdit = useCallback(() => {
        if (editingCommentId && editContent.trim()) {
            onEditComment(editingCommentId, editContent.trim());
        }
        setEditingCommentId(null);
        setEditContent("");
    }, [editingCommentId, editContent, onEditComment]);

    const handleCancelEdit = useCallback(() => {
        setEditingCommentId(null);
        setEditContent("");
    }, []);

    const submitButtonText = useMemo(() => {
        if (isSubmitting) return "Submitting...";
        if (selectedCommentIds.size === 0) return `Submit all (${comments.length})`;
        return `Submit selected (${selectedCommentIds.size})`;
    }, [isSubmitting, selectedCommentIds.size, comments.length]);

    return (
        <div className={cn("flex h-full flex-col", className)}>
            {/* Submit confirmation modal */}
            <Dialog open={showSubmitForm} onOpenChange={(open) => !open && handleCancelSubmit()}>
                <DialogContent data-track-location="review_submit_dialog">
                    <DialogHeader>
                        <DialogTitle>Submit review</DialogTitle>
                    </DialogHeader>
                    <DialogBody>
                        <textarea
                            value={additionalContext}
                            onChange={(e) => setAdditionalContext(e.target.value)}
                            placeholder="Add additional context (optional)..."
                            className="min-h-[100px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-3 text-sm text-content-primary placeholder:text-content-muted focus:border-border-strong focus:outline-none"
                            rows={4}
                            autoFocus
                        />
                    </DialogBody>
                    <DialogFooter>
                        <Button
                            variant="secondary"
                            onClick={handleCancelSubmit}
                            disabled={isSubmitting}
                            data-tracking-id="cancel"
                        >
                            Cancel
                        </Button>
                        <Button
                            onClick={handleConfirmSubmit}
                            disabled={isSubmitting || selectedComments.length === 0}
                            LeadingIcon={IconArrowUp}
                            data-tracking-id="confirmsubmit"
                        >
                            {isSubmitting ? "Submitting..." : "Submit"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Main content area */}
            <ScrollArea orientation="vertical" className="flex-1">
                <div className="mx-auto flex w-full max-w-[700px] flex-col gap-6 p-4">
                    {/* Git Status Section */}
                    <GitStatusPanelCompact environmentId={environment.id} />

                    {/* Review Comments Section - only show when there are comments */}
                    {comments.length > 0 && (
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <Text className="text-md font-medium">Review Comments</Text>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="secondary"
                                        size="sm"
                                        onClick={onDismissReview}
                                        disabled={isSubmitting || showSubmitForm}
                                        data-tracking-id="dismiss-review-comments"
                                    >
                                        Dismiss
                                    </Button>
                                    <Button
                                        size="sm"
                                        onClick={handleSubmitClick}
                                        disabled={isSubmitting || showSubmitForm}
                                        LeadingIcon={IconArrowUp}
                                        data-tracking-id="submit-review-comments"
                                    >
                                        {submitButtonText}
                                    </Button>
                                </div>
                            </div>
                            <div className="flex flex-col gap-3">
                                {comments.map((comment) => (
                                    <ReviewCommentCard
                                        key={comment.id}
                                        environmentId={environment.id}
                                        comment={comment}
                                        isEditing={editingCommentId === comment.id}
                                        editContent={editContent}
                                        onEditContentChange={setEditContent}
                                        onStartEdit={() => handleStartEdit(comment)}
                                        onSaveEdit={handleSaveEdit}
                                        onCancelEdit={handleCancelEdit}
                                        onRemove={() => onRemoveComment(comment.id)}
                                        isSelected={selectedCommentIds.has(comment.id)}
                                        onToggleSelect={() => toggleCommentSelection(comment.id)}
                                        onUpdateCodeContext={(hash, lines) => onUpdateCodeContext?.(comment.id, hash, lines)}
                                    />
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Code Changes Section */}
                    {gitStatus && (
                        <div className="flex flex-col gap-3">
                            <div className="flex items-center justify-between">
                                <Text className="text-md font-medium">Code Changes</Text>
                                {gitStatus.changedFiles.length > 0 && (
                                    <div className="flex items-center gap-2">
                                        {onStartReview && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={onStartReview}
                                                disabled={isStartReviewDisabled}
                                                data-tracking-id="start-review-code-changes"
                                            >
                                                Review
                                            </Button>
                                        )}
                                        {onCreatePullRequest && (
                                            <Button
                                                variant="secondary"
                                                size="sm"
                                                onClick={onCreatePullRequest}
                                                disabled={isCreatePullRequestDisabled}
                                                data-tracking-id="create-pull-request"
                                            >
                                                <GitPullRequest size={16} className="shrink-0" />
                                                Create PR
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                            {gitStatus.changedFiles.length > 0 ? (
                                <FileChangeList
                                    environment={environment}
                                    gitStatus={gitStatus}
                                    openedFiles={openedFiles}
                                    onOpenedFilesChange={setOpenedFiles}
                                    enableReviewComments={true}
                                    onLineSelectionForComment={onLineSelectionForComment}
                                    comments={comments}
                                    onCommentClick={onNavigateToComment}
                                    pendingCommentSelection={pendingSelection}
                                    onAddComment={onAddComment}
                                    onCancelComment={onClearPendingSelection}
                                />
                            ) : (
                                <div className="flex flex-col items-center gap-2 rounded-lg border border-border-base bg-surface-secondary p-6">
                                    <Text className="text-center text-sm text-content-secondary">
                                        No uncommitted changes
                                    </Text>
                                </div>
                            )}
                        </div>
                    )}
                </div>
            </ScrollArea>
        </div>
    );
};

type ReviewCommentCardProps = {
    environmentId: string;
    comment: ReviewComment;
    isEditing: boolean;
    editContent: string;
    onEditContentChange: (content: string) => void;
    onStartEdit: () => void;
    onSaveEdit: () => void;
    onCancelEdit: () => void;
    onRemove: () => void;
    isSelected: boolean;
    onToggleSelect: () => void;
    onUpdateCodeContext: (hash: string, lines: CachedCodeLine[]) => void;
};

const ReviewCommentCard: FC<ReviewCommentCardProps> = ({
    environmentId,
    comment,
    isEditing,
    editContent,
    onEditContentChange,
    onStartEdit,
    onSaveEdit,
    onCancelEdit,
    onRemove,
    isSelected,
    onToggleSelect,
    onUpdateCodeContext,
}) => {
    const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
            e.preventDefault();
            onSaveEdit();
        } else if (e.key === "Escape") {
            e.preventDefault();
            onCancelEdit();
        }
    };

    return (
        <div
            id={`review-comment-${comment.id}`}
            className={cn(
                "rounded-lg border bg-surface-secondary",
                isSelected ? "border-content-primary" : "border-border-base",
            )}
        >
            {/* Code context preview - at top of card */}
            <CodeContextPreview
                environmentId={environmentId}
                filePath={comment.filePath}
                startLine={comment.startLine}
                endLine={comment.endLine}
                contextLines={3}
                originalContentHash={comment.contentHash}
                onCodeContextComputed={onUpdateCodeContext}
                cachedCodeLines={comment.cachedCodeLines}
                showFullPath
                leadingElement={
                    <Checkbox
                        checked={isSelected}
                        onCheckedChange={onToggleSelect}
                        aria-label="Select comment"
                    />
                }
            >
                <div className="flex items-center gap-1">
                    <span
                        className={cn(
                            "rounded px-1.5 py-0.5 text-xs",
                            comment.author === "user"
                                ? "bg-surface-accent text-content-muted"
                                : "bg-surface-success-subtle text-content-success",
                        )}
                    >
                        {comment.author === "user" ? "You" : "Ona"}
                    </span>
                    {!isEditing && (
                        <>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="size-6 p-0"
                                onClick={onStartEdit}
                                aria-label="Edit comment"
                                data-tracking-id="edit-review-comment"
                            >
                                <IconEdit size="sm" />
                            </Button>
                            <Button
                                variant="ghost"
                                size="sm"
                                className="size-6 p-0 text-content-destructive hover:text-content-destructive"
                                onClick={onRemove}
                                aria-label="Remove comment"
                                data-tracking-id="remove-review-comment"
                            >
                                <IconTrash size="sm" />
                            </Button>
                        </>
                    )}
                </div>
            </CodeContextPreview>

            {/* Content */}
            <div className="p-3">
                {isEditing ? (
                    <div className="flex flex-col gap-2">
                        <textarea
                            value={editContent}
                            onChange={(e) => onEditContentChange(e.target.value)}
                            onKeyDown={handleKeyDown}
                            className="min-h-[60px] w-full resize-none rounded-md border border-border-base bg-surface-primary p-2 text-sm text-content-primary focus:border-content-link focus:outline-none focus:ring-1 focus:ring-content-link"
                            autoFocus
                        />
                        <div className="flex justify-end gap-2">
                            <Button
                                variant="secondary"
                                size="sm"
                                onClick={onCancelEdit}
                                data-tracking-id="cancel-edit-comment"
                            >
                                Cancel
                            </Button>
                            <Button
                                size="sm"
                                onClick={onSaveEdit}
                                disabled={!editContent.trim()}
                                data-tracking-id="save-edit-comment"
                            >
                                Save
                            </Button>
                        </div>
                    </div>
                ) : (
                    <Markdown content={comment.content} className="text-base text-content-primary" />
                )}
            </div>
        </div>
    );
};
